import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Inbox, Loader2, Mail, MailOpen, Trash2, Building2, Reply, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function SupportInbox() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { setMessages((await api.get("/admin/contact-messages")).data.messages); }
    catch { toast.error("Failed to load messages"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleRead = async (m) => {
    const next = !m.read;
    setBusy(m.id);
    try {
      await api.post(`/admin/contact-messages/${m.id}/read`, { read: next });
      setMessages((list) => list.map((x) => (x.id === m.id ? { ...x, read: next } : x)));
    } catch { toast.error("Couldn't update message"); }
    finally { setBusy(null); }
  };

  const remove = async (m) => {
    setBusy(m.id);
    try {
      await api.delete(`/admin/contact-messages/${m.id}`);
      setMessages((list) => list.filter((x) => x.id !== m.id));
      toast.success("Message deleted");
    } catch { toast.error("Delete failed"); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-40"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;

  const unread = messages.filter((m) => !m.read).length;
  const filtered = messages.filter((m) => filter === "all" || (filter === "unread" ? !m.read : m.read));

  return (
    <div className="p-6 lg:p-8 max-w-[1000px] mx-auto" data-testid="support-inbox-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">
          <Inbox className="h-7 w-7 text-amber-400" /> Support Inbox
          {unread > 0 && <span data-testid="unread-badge" className="text-sm font-semibold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">{unread} unread</span>}
        </h1>
        <p className="text-gray-400 mt-1.5 text-sm">Messages sent through the public contact form.</p>
      </div>

      <div className="mb-5 flex gap-2">
        {["all", "unread", "read"].map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={"px-3.5 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors border " + (filter === f ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "text-gray-400 border-gray-800 hover:text-white")}>
            {f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-800 bg-[#0E131F] py-20 text-center" data-testid="inbox-empty">
          <Inbox className="mx-auto h-10 w-10 text-gray-600" />
          <p className="mt-3 text-gray-400">No messages here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m, i) => (
            <motion.div
              key={m.id}
              data-testid={`message-${m.id}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.3) }}
              className={"rounded-xl border bg-[#0E131F] p-5 " + (m.read ? "border-gray-800" : "border-amber-500/30")}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!m.read && <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" title="Unread" />}
                    <span className="font-semibold text-white">{m.name}</span>
                    <a href={`mailto:${m.email}`} className="text-sm text-amber-400 hover:underline">{m.email}</a>
                    {m.organization && <span className="text-xs text-gray-500 inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{m.organization}</span>}
                  </div>
                  <p className="mt-2 text-sm text-gray-300 whitespace-pre-wrap">{m.message}</p>
                  <p className="mt-2 text-xs text-gray-600 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(m.created_at)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <a data-testid={`reply-${m.id}`} href={`mailto:${m.email}?subject=Re: Your StreamWorship support request`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 text-xs font-medium transition-colors"><Reply className="h-3.5 w-3.5" /> Reply</a>
                  <button data-testid={`toggle-read-${m.id}`} onClick={() => toggleRead(m)} disabled={busy === m.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 text-gray-300 border border-gray-700 hover:text-white text-xs font-medium transition-colors disabled:opacity-50" title={m.read ? "Mark as unread" : "Mark as read"}>
                    {m.read ? <Mail className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />}
                    {m.read ? "Unread" : "Read"}
                  </button>
                  <button data-testid={`delete-${m.id}`} onClick={() => remove(m)} disabled={busy === m.id} className="inline-flex items-center px-2.5 py-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 border border-gray-700 hover:border-red-500/30 transition-colors disabled:opacity-50" title="Delete">
                    {busy === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
